import React, { useState, useEffect } from 'react'
import styled from 'styled-typed'
import { History } from 'history'
import { Navigation } from 'navigation'
import { match as RouteMatch } from 'react-router-dom'
import { delay } from 'utils'
import { ActionButton, ActionButtonA } from 'uiComponents/buttons'
import { withNavigation } from 'hocs'
import { ButtonWrapper, TextWithHorizontalLine } from 'uiComponents/pageElements'
import { MessageKind } from 'uiComponents/messages'
import { ContainerBody, PageActions } from 'uiComponents/settingsContainer'
import { PageLoader } from 'uiComponents/loaders'
import { Col, Row } from 'uiComponents/flex'
import { FormItem, FormItemName } from 'uiComponents/form/formElements'
import { TimePicker, TextInput } from 'uiComponents/input'
import { OpeningTimesServiceContext } from './context'
import { OpeningTimesUnit, Exception } from './openingTimesService'
import { OpeningTimesExceptions } from './exceptionsList'
import Infotip from 'uiComponents/infotip'
import { isAfter, isBefore } from 'date-fns'
import { SecondaryText } from 'uiComponents/typography'
import { parseTime } from 'utils/dates'
import { useTranslation } from 'translations'

const Section = styled(Col)`
    margin-bottom: 2em;
`

interface OpeningTimesPageParams {
    accountSlug: string
    id: string
}

interface OpeningTimesFormProps {
    accountSlug: string
    match: RouteMatch<OpeningTimesPageParams>
    history: History
    navigation: Navigation
    setActiveHeader: (header: string) => void
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
}

function OpeningTimesForm(props: OpeningTimesFormProps) {
    const { t } = useTranslation()
    const didMountRef = React.useRef(false)
    const openingTimesService = React.useContext(OpeningTimesServiceContext)
    const [loading, setLoading] = useState<boolean>(false)
    const [locationName, setLocationName] = useState<string>('')
    const [openingTime, setOpeningTime] = useState<string>('00:00')
    const [closingTime, setClosingTime] = useState<string>('00:00')
    const [sellUntilTime, setSellUntilTime] = useState<string | null>(null)
    const [overrides, setOverrides] = useState<Exception[]>([])
    const [openingTimeError, setOpeningTimeError] = useState<string | null>(null)
    const [closingTimeError, setClosingTimeError] = useState<string | null>(null)
    const [sellUntilTimeError, setSellUntilTimeError] = useState<string | null>(null)

    const fetchOpeningTimesUnitData = async (id: string) => {
        setLoading(true)
        try {
            const data = await openingTimesService.getOpeningTimesUnit(props.accountSlug, id)
            setDataToState(data)
        } catch {
            props.replaceMessages(
                'server_error',
                'error',
                t('venue.openingTimes.form.fetchError'),
            )
        } finally {
            setLoading(false)
        }
    }

    const setDataToState = (data: OpeningTimesUnit) => {
        setLocationName(data.locationName)
        setOpeningTime(data.openingTime || '00:00')
        setClosingTime(data.closingTime || '00:00')
        setSellUntilTime(data.sellUntilTime)
        setOverrides(data.exceptions)
    }

    const validateTimes = () => {
        setOpeningTimeError(
            isAfter(parseTime(openingTime), parseTime(closingTime)) ? t('venue.openingTimes.form.openingTimeError') : null,
        )
        setClosingTimeError(
            isBefore(parseTime(closingTime), parseTime(openingTime)) ? t('venue.openingTimes.form.closingTimeError') : null,
        )
        setSellUntilTimeError(
            sellUntilTime && isAfter(parseTime(sellUntilTime), parseTime(closingTime))
                ? t('venue.openingTimes.form.sellUntilTimeError')
                : null,
        )
    }

    useEffect(validateTimes, [openingTime, closingTime, sellUntilTime])

    useEffect(() => {
        const timeSlotId = props.match.params.id
        props.setActiveHeader(t('venue.openingTimes.form.title'))
        if (!!timeSlotId) {
            fetchOpeningTimesUnitData(timeSlotId)
        }
    }, [])

    useEffect(() => {
        if (didMountRef.current) {
            props.history.push(`/account/${props.accountSlug}/venue/opening_times/locations`)
        } else {
            didMountRef.current = true
        }
    }, [props.accountSlug])

    const isDataValid = () => !openingTimeError && !closingTimeError && !sellUntilTimeError

    const onOpeningTimesSave = async () => {
        if (isDataValid()) {
            setLoading(true)
            const payload = {
                openingTime,
                closingTime,
                sellUntilTime,
            }
            try {
                await openingTimesService.updateOpeningTimesUnit(
                    props.accountSlug,
                    payload,
                    props.match.params.id,
                    locationName,
                )
                setLoading(false)
                props.history.push(`/account/${props.accountSlug}/venue/opening_times/locations`)
                props.replaceMessages('success', 'success', t('venue.openingTimes.form.saveSuccess'))
                await delay(3000)
                props.removeAllMessages()
            } catch {
                setLoading(false)
                props.replaceMessages(
                    'server_error',
                    'error',
                    t('venue.openingTimes.form.saveError'),
                )
            }
        } else {
            props.replaceMessages('validation_error', 'error', t('venue.openingTimes.validationErrors'))
        }
    }

    return (
        <div style={{ position: 'relative' }}>
            {loading && <PageLoader style={{ paddingBottom: '4em' }} />}
            {!loading && (
                <ContainerBody id="openingHours-form" style={{ flexDirection: 'column' }}>
                    <Row>
                        <Col span={6}>
                            <FormItem htmlFor="name">
                                <FormItemName style={{ display: 'flex' }}>{t('venue.openingTimes.form.locationNameLabel')}</FormItemName>
                                <TextInput value={locationName} block disabled />
                            </FormItem>
                        </Col>
                        <Col span={6} />
                    </Row>
                    <Row>
                        <Section span={6}>
                            <Row>
                                <Col span={6}>
                                    <FormItem htmlFor="venueOpens">
                                        <FormItemName>{t('venue.openingTimes.form.opensLabel')}</FormItemName>
                                        <TimePicker
                                            id="venueOpens"
                                            value={openingTime}
                                            onChange={(time) => time && setOpeningTime(time)}
                                            style={{ fontWeight: 'lighter' }}
                                            required={true}
                                            errorMessage={openingTimeError}
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={6}>
                                    <FormItem htmlFor="venueCloses">
                                        <FormItemName>{t('venue.openingTimes.form.closesLabel')}</FormItemName>
                                        <TimePicker
                                            id="venueCloses"
                                            value={closingTime}
                                            onChange={(time) => time && setClosingTime(time)}
                                            style={{ fontWeight: 'lighter' }}
                                            required={true}
                                            errorMessage={closingTimeError}
                                        />
                                    </FormItem>
                                </Col>
                            </Row>
                        </Section>
                        <Section span={6}>
                            <FormItem htmlFor="ticketsSoldUntil">
                                <FormItemName>
                                    {t('venue.openingTimes.form.sellUntilLabel')}&nbsp;
                                    <SecondaryText>{t('venue.openingTimes.optional')}</SecondaryText>
                                    <Infotip pointer="right">
                                        {t('venue.openingTimes.form.sellUntilTooltip')}
                                    </Infotip>
                                </FormItemName>
                                <TimePicker
                                    id="ticketsSoldUntil"
                                    value={sellUntilTime}
                                    onChange={(time) => {
                                        setSellUntilTime(time)
                                        validateTimes()
                                    }}
                                    style={{ fontWeight: 'lighter' }}
                                    errorMessage={sellUntilTimeError}
                                />
                            </FormItem>
                        </Section>
                    </Row>
                    <Col span={12}>
                        <ButtonWrapper>
                            <ActionButtonA
                                size="large"
                                href={`/account/${props.accountSlug}/venue/opening_times`}
                                secondary
                                style={{ marginRight: '1.5em' }}
                                kind="action"
                            >
                                {t('common.cancel')}
                            </ActionButtonA>
                            <ActionButton id="save-opening-times" size="large" onClick={onOpeningTimesSave}>
                                {t('common.save')}
                            </ActionButton>
                        </ButtonWrapper>
                    </Col>
                    <TextWithHorizontalLine text={t('venue.openingTimes.form.exceptionsHeading')} alignment="left" />
                    <OpeningTimesExceptions
                        accountSlug={props.accountSlug}
                        overrides={overrides}
                        openingTimesId={props.match.params.id}
                    />
                    <PageActions>
                        <ActionButtonA
                            id="new-exception"
                            size="large"
                            href={`/account/${props.accountSlug}/venue/opening_times/exception/new/default/${props.match.params.id}`}
                        >
                            {t('venue.openingTimes.form.addException')}
                        </ActionButtonA>
                    </PageActions>
                </ContainerBody>
            )}
        </div>
    )
}

export default withNavigation(OpeningTimesForm)
